/**
 * Cache Hydration Script
 *
 * Hydrates cached pages with personalized content by fetching:
 * 1. /cart.js — cart count, currency
 * 2. ?section_id=customer-data-provider — customer info (FreshChat, GTM, wishlist, forms)
 * 3. ?section_id=csrf-token-provider — fresh CSRF tokens for all forms
 *
 * All three requests run in parallel on DOMContentLoaded.
 */
(function () {
  'use strict';

  var path = window.location.pathname;

  // Skip hydration only on cart and checkout (they render personalized content server-side)
  if (
    path === '/cart' ||
    path.startsWith('/checkout')
  ) {
    return;
  }

  // ── Cart Hydration ──
  function hydrateCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) return;
        return res.json();
      })
      .then(function (cart) {
        if (!cart) return;

        // Update cart icon bubble
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          if (cart.item_count > 0) {
            bubble.innerHTML =
              '<div class="header__cart-count absolute bg-theme-text text-theme-bg text-center">' +
              (cart.item_count < 10000
                ? '<span aria-hidden="true" class="header__cart-count-number">' + cart.item_count + '</span>'
                : '') +
              '<span class="visually-hidden">' + cart.item_count + ' item' + (cart.item_count !== 1 ? 's' : '') + ' in cart</span>' +
              '</div>';
          } else {
            bubble.innerHTML = '';
          }
        }

        // Toggle cart shake animation when empty
        var cartIcon = document.getElementById('cart-icon');
        if (cartIcon && cartIcon.dataset.cartShakeEnabled === 'true' && cart.item_count === 0) {
          cartIcon.classList.remove('header__icon--cart-shake', 'has-motion');
        }

        // Patch currency in window.theme.settings
        if (cart.currency && window.theme && window.theme.settings) {
          window.theme.settings.currency = cart.currency;
        }

        document.dispatchEvent(
          new CustomEvent('cache:cart-hydrated', { detail: { cart: cart } })
        );
      })
      .catch(function (e) {
        console.error('[cache-hydration] Cart hydration failed:', e);
      });
  }

  // ── Customer Hydration ──
  function hydrateCustomer() {
    return fetch('/?section_id=customer-data-provider', {
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) return;
        return res.text();
      })
      .then(function (html) {
        if (!html) return;

        var tmpl = document.createElement('template');
        tmpl.innerHTML = html;
        var dataEl = tmpl.content.querySelector('#customer-json');
        if (!dataEl) return;

        var customer;
        try {
          customer = JSON.parse(dataEl.textContent);
        } catch (e) {
          return;
        }

        if (!customer || !customer.logged_in) return;

        // FreshChat button — set both dataset AND instance properties
        // (the component caches dataset values in connectedCallback)
        var fcBtn = document.querySelector('freshchat-button');
        if (fcBtn) {
          if (customer.id) {
            fcBtn.dataset.customerId = customer.id;
            fcBtn.customerId = String(customer.id);
          }
          if (customer.name) {
            fcBtn.dataset.firstName = customer.name;
            fcBtn.firstName = customer.name;
          }
          if (customer.email) {
            fcBtn.dataset.email = customer.email;
            fcBtn.email = customer.email;
          }
          if (customer.phone) {
            fcBtn.dataset.phone = customer.phone;
            fcBtn.phone = customer.phone;
          }
        }

        // Wishlist
        if (window.wishlist && window.wishlist.user) {
          window.wishlist.user.email = customer.email || null;
        }

        // Pre-fill form inputs by autocomplete attribute
        document.querySelectorAll('input[autocomplete="name"]').forEach(function (input) {
          if (!input.value && customer.name) input.value = customer.name;
        });
        document.querySelectorAll('input[autocomplete="email"]').forEach(function (input) {
          if (!input.value && customer.email) input.value = customer.email;
        });
        document.querySelectorAll('input[autocomplete="tel"]').forEach(function (input) {
          if (!input.value && customer.phone) input.value = customer.phone;
        });

        // Product reviews widget
        var reviewWidget = document.querySelector('[data-widget-type="product-reviews"]');
        if (reviewWidget) {
          if (customer.email) reviewWidget.dataset.customerEmail = customer.email;
          if (customer.name) reviewWidget.dataset.customerFullName = customer.name;
        }

        document.dispatchEvent(
          new CustomEvent('cache:customer-hydrated', { detail: { customer: customer } })
        );
      })
      .catch(function (e) {
        console.error('[cache-hydration] Customer hydration failed:', e);
      });
  }

  // ── CSRF Token Hydration ──
  function hydrateCsrfTokens() {
    // Only hydrate if there are forms with authenticity_token on the page
    var tokenInputs = document.querySelectorAll('form input[name="authenticity_token"]');
    if (tokenInputs.length === 0) return Promise.resolve();

    return fetch('/?section_id=csrf-token-provider', {
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) return;
        return res.text();
      })
      .then(function (html) {
        if (!html) return;

        var tmpl = document.createElement('template');
        tmpl.innerHTML = html;
        var freshToken = tmpl.content.querySelector('input[name="authenticity_token"]');
        if (!freshToken) return;

        tokenInputs.forEach(function (input) {
          input.value = freshToken.value;
        });

        document.dispatchEvent(new CustomEvent('cache:forms-hydrated'));
      })
      .catch(function (e) {
        console.error('[cache-hydration] CSRF hydration failed:', e);
      });
  }

  // ── Cart Drawer Hydration (on first open) ──
  function hydrateCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var refreshed = false;
    drawer.addEventListener('on:cart-drawer:before-open', function () {
      if (refreshed) return;
      refreshed = true;
      if (typeof drawer.refresh === 'function') {
        drawer.refresh();
      }
    });
  }

  // ── Execute all hydration in parallel ──
  function runHydration() {
    hydrateCart();
    hydrateCustomer();
    hydrateCsrfTokens();
    hydrateCartDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runHydration);
  } else {
    runHydration();
  }
})();
